package dev.lingvo.shared.agent

import dev.lingvo.shared.Role
import dev.lingvo.shared.TokenScope

/**
 * The tools that change something, and what each one is allowed to change.
 *
 * The classes are the enforcement spine, not documentation: `AgentGate` reads [AgentToolSpec.safety]
 * to decide whether an autonomy level may make the call at all. A tool that shipped unclassified
 * would be a tool the gate could not judge, which is why the catalogue test fails the build rather
 * than defaulting one.
 *
 * **Nothing here reaches APPROVED**, on any transport, at any autonomy level. There is no class that
 * expresses it and no tool that asks for it — see `AgentWrites`, which throws rather than clamping,
 * because a caller that asks has misunderstood the surface.
 */
internal object AgentWriteTools {

    val TOOLS: List<AgentToolSpec> = listOf(
        AgentToolSpec(
            name = "propose_translation",
            summary = "Suggest a translation. It waits for a person to accept it.",
            safety = ToolSafety.PROPOSE,
            scopes = setOf(TokenScope.STRINGS_WRITE),
            minimumRole = Role.TRANSLATOR,
            input = AgentSchema.obj(
                "project" to PROJECT_ARGUMENT,
                "locale" to AgentSchema.string("Target locale, e.g. de."),
                "key" to AgentSchema.string("Key name."),
                "value" to AgentSchema.string("The proposed text."),
                "rationale" to AgentSchema.string(
                    "Why you chose this wording, in one or two sentences. At most 500 characters; " +
                        "longer is refused rather than cut in half.",
                ),
                "confidence" to AgentSchema.string(
                    "How sure you are. A reviewer deciding four thousand strings reads the low ones " +
                        "first, and this is the only signal that can tell them which those are.",
                    values = listOf("high", "medium", "low"),
                ),
                required = listOf("locale", "key", "value"),
            ),
            // Proposing the same text twice leaves one queue entry's worth of work for a reviewer,
            // not two: the row is keyed by key, locale and author.
            idempotent = true,
            transports = setOf(Transport.LOCAL, Transport.REMOTE),
        ),
        AgentToolSpec(
            name = "propose_plural",
            summary = "Suggest a plural translation. Every category the language uses must be " +
                "present; a partial set is refused rather than stored.",
            safety = ToolSafety.PROPOSE,
            scopes = setOf(TokenScope.STRINGS_WRITE),
            minimumRole = Role.TRANSLATOR,
            input = AgentSchema.obj(
                "project" to PROJECT_ARGUMENT,
                "locale" to AgentSchema.string("Target locale, e.g. ru."),
                "key" to AgentSchema.string("Key name."),
                // **An object, and now declared as one.** The remote schema said `string` while the
                // handler did `as? JsonObject`, so a client that trusted the schema sent text and
                // was refused for a reason the schema had caused.
                "forms" to AgentSchema.stringMap(
                    "CLDR category to text, e.g. {\"one\":\"…\",\"few\":\"…\",\"other\":\"…\"}. " +
                        "Call locale_info first: a partial set looks finished and breaks only for " +
                        "certain numbers.",
                ),
                "rationale" to AgentSchema.string(
                    "Why you chose this wording, in one or two sentences. At most 500 characters; " +
                        "longer is refused rather than cut in half.",
                ),
                "confidence" to AgentSchema.string(
                    "How sure you are. A reviewer deciding four thousand strings reads the low ones " +
                        "first, and this is the only signal that can tell them which those are.",
                    values = listOf("high", "medium", "low"),
                ),
                required = listOf("locale", "key", "forms"),
            ),
            transports = setOf(Transport.LOCAL, Transport.REMOTE),
        ),
        AgentToolSpec(
            name = "comment",
            summary = "Ask a question about a string. Changes no translation.",
            safety = ToolSafety.PROPOSE,
            scopes = setOf(TokenScope.STRINGS_WRITE),
            minimumRole = Role.TRANSLATOR,
            input = AgentSchema.obj(
                "project" to PROJECT_ARGUMENT,
                "key" to AgentSchema.string("Key name."),
                "body" to AgentSchema.string("The question."),
                required = listOf("key", "body"),
            ),
            // Asking the same question twice files it twice, which is what a person would expect.
            idempotent = false,
            transports = setOf(Transport.REMOTE),
        ),
        AgentToolSpec(
            name = "add_key",
            summary = "Add a new source string. Needs the connection to borrow an owner or admin.",
            safety = ToolSafety.STRUCTURAL,
            scopes = setOf(TokenScope.STRINGS_WRITE),
            // Structural change is what the product *says*, not how it is translated, so the
            // connection must borrow authority a translator does not have. Checked by the gate as
            // well as hidden from the manifest: a client that remembers the tool from a previous
            // session is refused rather than obeyed.
            minimumRole = Role.ADMIN,
            input = AgentSchema.obj(
                "project" to PROJECT_ARGUMENT,
                "file" to AgentSchema.string("File path the key belongs in, as the project lists it."),
                "key" to AgentSchema.string("Key name."),
                "value" to AgentSchema.string("The source text."),
                // **The note only exists at this moment.** The agent writing the feature is the one
                // party who knows what the string is for; discarding it here guarantees the string
                // reaches a translator bare, and nobody can reconstruct it afterwards.
                "context" to AgentSchema.string(
                    "What this string is and where it appears, for whoever translates it.",
                ),
                "max_length" to AgentSchema.integer("Characters the control can hold.", minimum = 1),
                "tags" to AgentSchema.string("Comma-separated tags."),
                required = listOf("file", "key", "value"),
            ),
            idempotent = false,
            transports = setOf(Transport.REMOTE),
        ),
        AgentToolSpec(
            name = "set_key_context",
            summary = "Write down what an existing string is for: its developer context, its " +
                "length budget, its tags. The single highest-value thing a coding agent can do, " +
                "because it is the only moment the answer is in front of anybody.",
            safety = ToolSafety.STRUCTURAL,
            scopes = setOf(TokenScope.STRINGS_WRITE),
            // Separate from `add_key` deliberately: annotating a string that exists should not need
            // the authority to invent one. It borrows an admin because it changes what the product
            // *says about itself*, which the structural class is for.
            minimumRole = Role.ADMIN,
            input = AgentSchema.obj(
                "project" to PROJECT_ARGUMENT,
                "key" to AgentSchema.string("Key name."),
                "context" to AgentSchema.string(
                    "What this string is and where it appears — the note a translator reads before " +
                        "writing anything. Omit to leave the stored note alone.",
                ),
                "max_length" to AgentSchema.integer(
                    "Characters the control can hold. Omit to leave it alone.",
                    minimum = 1,
                ),
                "tags" to AgentSchema.string(
                    "Comma-separated tags. Omit to leave them alone.",
                ),
                required = listOf("key"),
            ),
            transports = setOf(Transport.LOCAL, Transport.REMOTE),
        ),
        AgentToolSpec(
            name = "push_strings",
            summary = "Replace a file's strings from entries. Obsoleting keys requires " +
                "confirm_obsolete_count to match exactly what this push would remove.",
            safety = ToolSafety.STRUCTURAL,
            scopes = setOf(TokenScope.FILES_WRITE),
            minimumRole = Role.TRANSLATOR,
            input = AgentSchema.obj(
                "project" to PROJECT_ARGUMENT,
                "file" to AgentSchema.string("File path, as the project lists it."),
                "locale" to AgentSchema.string("Locale these entries are in."),
                "entries" to AgentSchema.stringMap("Key name to text."),
                "confirm_obsolete_count" to AgentSchema.integer(
                    "How many keys this push should obsolete. Must equal what a dry run reported, " +
                        "exactly; any obsoletion at all requires it.",
                    minimum = 0,
                ),
                required = listOf("file", "locale", "entries"),
            ),
            transports = setOf(Transport.REMOTE),
        ),
        AgentToolSpec(
            name = "machine_translate",
            summary = "Translate the empty strings in a locale with a machine provider. Costs " +
                "credits, and returns a job handle rather than holding the connection open.",
            safety = ToolSafety.SPEND,
            scopes = setOf(TokenScope.MT_SPEND),
            minimumRole = Role.ADMIN,
            input = AgentSchema.obj(
                "project" to PROJECT_ARGUMENT,
                "locale" to AgentSchema.string("Target locale, e.g. de."),
                "provider" to AgentSchema.string("Provider id, e.g. deepl."),
                required = listOf("locale", "provider"),
            ),
            idempotent = false,
            transports = setOf(Transport.REMOTE),
        ),

        // --- The filesystem pair. LOCAL only: the remote server has no filesystem and must not
        // grow one — a tool that took a file body would need a path, an encoding and a format guess,
        // and every one of those is a decision the agent's own repository has already made.

        AgentToolSpec(
            name = "pull",
            summary = "Write translations from the server into the repository. Defaults to a dry run.",
            safety = ToolSafety.WRITE,
            scopes = setOf(TokenScope.FILES_WRITE),
            minimumRole = Role.TRANSLATOR,
            input = AgentSchema.obj(
                "dry_run" to AgentSchema.boolean(
                    "Set false to actually write files.",
                    default = true,
                ),
                "locale" to AgentSchema.string("Limit to one locale."),
            ),
            idempotent = false,
            dryRunnable = true,
            transports = setOf(Transport.LOCAL),
        ),
        AgentToolSpec(
            name = "push",
            summary = "Upload source strings from the repository. Defaults to a dry run. Never " +
                "obsoletes keys unless obsolete_missing is true AND confirm_obsolete_count matches " +
                "the plan.",
            safety = ToolSafety.STRUCTURAL,
            scopes = setOf(TokenScope.FILES_WRITE),
            minimumRole = Role.TRANSLATOR,
            input = AgentSchema.obj(
                "dry_run" to AgentSchema.boolean("Set false to actually push.", default = true),
                "obsolete_missing" to AgentSchema.boolean(
                    "Mark server keys missing from the repository as obsolete.",
                    default = false,
                ),
                "confirm_obsolete_count" to AgentSchema.integer(
                    "Must equal the number a dry run reported, exactly.",
                    minimum = 0,
                ),
            ),
            idempotent = false,
            dryRunnable = true,
            transports = setOf(Transport.LOCAL),
        ),
    )
}
